import React, { createContext, useContext, useReducer, useEffect } from 'react';

// --- Game Data Models ---
export const LEVELS = [
  {
    id: 1,
    name: 'Rookie Apprentice',
    description: 'Begin your journey as a new apprentice. Complete daily tasks to earn XP and coins.',
    unlockXP: 0,
    tasks: [
      {
        id: 'safety_gear_quiz',
        title: 'Safety Gear Quiz',
        description: 'Identify the correct safety gear for each scenario.',
        type: 'quiz',
        difficulty: 'easy',
        xpReward: 10, // per correct
        coinReward: 2, // per correct
        timeLimit: 15, // seconds per question
        questions: [
          {
            question: 'Which gear is essential when working with sparks?',
            options: ['Helmet', 'Gloves', 'Goggles', 'Shoes'],
            correctAnswer: 'Goggles',
            image: '',
          },
          {
            question: 'What should you wear to protect your feet in a workshop?',
            options: ['Sandals', 'Shoes', 'Slippers', 'Socks'],
            correctAnswer: 'Shoes',
            image: '',
          },
          {
            question: 'Which item protects your hands from sharp objects?',
            options: ['Helmet', 'Gloves', 'Goggles', 'Shoes'],
            correctAnswer: 'Gloves',
            image: '',
          },
          {
            question: 'What is worn to protect your head from falling objects?',
            options: ['Helmet', 'Gloves', 'Goggles', 'Shoes'],
            correctAnswer: 'Helmet',
            image: '',
          },
          {
            question: 'Which gear is used to protect your eyes?',
            options: ['Helmet', 'Gloves', 'Goggles', 'Shoes'],
            correctAnswer: 'Goggles',
            image: '',
          },
        ],
      },
      {
        id: 'tool_drag_drop',
        title: 'Tool Identification Drag & Drop',
        description: 'Drag each tool image to its correct name.',
        type: 'drag_drop',
        difficulty: 'easy',
        xpReward: 20, // per correct
        coinReward: 5, // overall
        questions: [], // will be handled by the minigame
      },
      {
        id: 'scenario_decision',
        title: 'Quick Scenario Decision',
        description: 'Choose the best action for each real-world scenario.',
        type: 'scenario',
        difficulty: 'easy',
        xpReward: 15, // per correct
        coinReward: 3, // per correct
        questions: [
          {
            question: 'Oil spilled on floor, what action to take?',
            options: ['Ignore it', 'Clean it up', 'Call supervisor'],
            correctAnswer: 'Clean it up',
            image: '',
          },
        ],
      },
    ],
    majorMission: {
      id: 'mission1',
      title: 'First Day Scenario',
      description: 'Complete your first day safely and efficiently.',
      type: 'scenario',
      difficulty: 'medium',
      xpReward: 30,
      coinReward: 20,
      questions: [],
    },
    badge: { id: 'rookie', name: 'Rookie Badge', description: 'Completed Level 1' },
  },
  {
    id: 2,
    name: 'Skilled Worker',
    description: 'Prove your skills and take on more responsibility. New tasks and missions await.',
    unlockXP: 100,
    tasks: [
      {
        id: 'tool_timing_challenge',
        title: 'Tool Timing Challenge',
        description: 'Pick the right tool for each job as fast as you can!',
        type: 'timed_choice',
        difficulty: 'medium',
        xpReward: 20, // per correct
        coinReward: 5, // per correct
        timeLimit: 10, // seconds per question
        questions: [
          {
            question: 'Tighten bolt',
            options: ['Wrench', 'Hammer', 'Plier', 'Screwdriver'],
            correctAnswer: 'Wrench',
            image: '',
          },
          {
            question: 'Drive a nail',
            options: ['Wrench', 'Hammer', 'Plier', 'Screwdriver'],
            correctAnswer: 'Hammer',
            image: '',
          },
          {
            question: 'Cut wire',
            options: ['Wrench', 'Hammer', 'Plier', 'Screwdriver'],
            correctAnswer: 'Plier',
            image: '',
          },
        ],
      },
      {
        id: 'fix_machine_puzzle',
        title: 'Fix the Machine Puzzle',
        description: 'Drag the missing parts to fix the machine.',
        type: 'machine_puzzle',
        difficulty: 'medium',
        xpReward: 30,
        coinReward: 10,
        questions: [], // handled by the minigame
      },
    ],
    majorMission: {
      id: 'mission2',
      title: 'Workshop Challenge',
      description: 'Complete the workshop challenge to prove your skills.',
      type: 'puzzle',
      difficulty: 'hard',
      xpReward: 30,
      coinReward: 10,
      questions: [],
    },
    badge: { id: 'skilled', name: 'Skilled Worker Badge', description: 'Completed Level 2' },
  },
  {
    id: 'shift_readiness_quiz',
    title: 'Shift Readiness Quiz',
    description: 'Answer the readiness scenario before your shift.',
    type: 'shift_quiz',
    difficulty: 'easy',
    xpReward: 10,
    coinReward: 2,
    timeLimit: 20,
    questions: [
      {
        question: 'Before starting shift, what must you do?',
        options: ['Check tools', 'Wear safety gear', 'Both'],
        correctAnswer: 'Both',
        image: '',
      },
    ],
  },
  // ...future levels
];

const initialState = {
  currentLevel: 1,
  xp: 0,
  coins: 0,
  badges: [],
  completedTasks: {}, // { [taskId]: true }
  completedMissions: {}, // { [missionId]: true }
};

function gameReducer(state, action) {
  switch (action.type) {
    case 'COMPLETE_TASK': {
      const { levelId, task, xp=0, coins=0 } = action.payload;
      const newXP = state.xp + xp;
      const newCoins = state.coins + coins;
      // Check for level up
      let newLevel = state.currentLevel;
      for (const lvl of LEVELS) {
        if (newXP >= lvl.unlockXP) newLevel = lvl.id;
      }
      return {
        ...state,
        xp: newXP,
        coins: newCoins,
        currentLevel: newLevel,
        completedTasks: { ...state.completedTasks, [task.id]: true },
      };
    }
    case 'COMPLETE_MISSION': {
      const { levelId, mission } = action.payload;
      const newXP = state.xp + mission.xpReward;
      const newCoins = state.coins + mission.coinReward;
      let newLevel = state.currentLevel;
      for (const lvl of LEVELS) {
        if (newXP >= lvl.unlockXP) newLevel = lvl.id;
      }
      // Award badge if not already
      const level = LEVELS.find(l => l.id === levelId);
      let newBadges = state.badges;
      if (level && !state.badges.includes(level.badge.id)) {
        newBadges = [...state.badges, level.badge.id];
      }
      return {
        ...state,
        xp: newXP,
        coins: newCoins,
        currentLevel: newLevel,
        completedMissions: { ...state.completedMissions, [mission.id]: true },
        badges: newBadges,
      };
    }
    default:
      return state;
  }
}

const GameContext = createContext();

const LOCAL_KEY = () => {
  // Try to use user id if available
  try {
    const user = JSON.parse(localStorage.getItem('gogig_user'));
    if (user && user.id) return `gogig_game_state_${user.id}`;
  } catch {}
  return 'gogig_game_state_default';
};

export function GameProvider({ children }) {
  // Load from localStorage if available
  const [state, dispatch] = useReducer(gameReducer, initialState, (init) => {
    try {
      const saved = localStorage.getItem(LOCAL_KEY());
      if (saved) return { ...init, ...JSON.parse(saved) };
    } catch {}
    return init;
  });

  // Save to localStorage on every state change
  useEffect(() => {
    localStorage.setItem(LOCAL_KEY(), JSON.stringify(state));
  }, [state]);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
} 